import type { Message, RequestPart, ResponsePart } from '@sbc/domain-models/agents';
import type { ReactNode } from 'react';

import { MarkdownProse } from '@/components/markdown';
import { Badge } from '@/components/ui/badge';

function asText(value: unknown): string {
  return typeof value === 'string' ? value : JSON.stringify(value, null, 2);
}

// Parts have no id; key on a stable, data-dependent discriminator — toolCallId for
// the tool parts, the content otherwise — so React never falls back to the index.
function requestPartKey(part: RequestPart): string {
  return part.partKind === 'tool-return'
    ? `tool-return:${part.toolCallId}`
    : `${part.partKind}:${part.content}`;
}

function responsePartKey(part: ResponsePart): string {
  return part.partKind === 'tool-call'
    ? `tool-call:${part.toolCallId}`
    : `${part.partKind}:${part.content}`;
}

function PartBlock({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-1">
      <Badge variant="outline">{label}</Badge>
      {children}
    </div>
  );
}

function Prose({ children }: { children: string }) {
  return <MarkdownProse value={children} />;
}

function Code({ children }: { children: string }) {
  return (
    <pre className="max-h-60 overflow-auto rounded-md border bg-[var(--color-muted)] p-2 text-xs whitespace-pre-wrap">
      {children}
    </pre>
  );
}

function RequestPartView({ part }: { part: RequestPart }) {
  switch (part.partKind) {
    case 'system-prompt':
      return (
        <PartBlock label="system prompt">
          <Code>{part.content}</Code>
        </PartBlock>
      );
    case 'user-prompt':
      return (
        <PartBlock label="user">
          <Prose>{part.content}</Prose>
        </PartBlock>
      );
    case 'tool-return':
      return (
        <PartBlock label={`tool return · ${part.toolName}`}>
          <Code>{asText(part.content)}</Code>
        </PartBlock>
      );
    case 'retry-prompt':
      return (
        <PartBlock label="retry">
          <Prose>{part.content}</Prose>
        </PartBlock>
      );
    default:
      return null;
  }
}

function ResponsePartView({ part }: { part: ResponsePart }) {
  switch (part.partKind) {
    case 'text':
      return (
        <PartBlock label="assistant">
          <Prose>{part.content}</Prose>
        </PartBlock>
      );
    case 'thinking':
      return (
        <PartBlock label="thinking">
          <Prose>{part.content}</Prose>
        </PartBlock>
      );
    case 'tool-call':
      return (
        <PartBlock label={`tool call · ${part.toolName}`}>
          <Code>{asText(part.args)}</Code>
        </PartBlock>
      );
    default:
      return null;
  }
}

function MessageView({ message }: { message: Message }) {
  const isRequest = message.kind === 'request';
  return (
    <li
      className={
        isRequest ? 'rounded-md border p-3' : 'rounded-md border bg-[var(--color-muted)] p-3'
      }
    >
      <div className="mb-2 flex items-center justify-between text-xs text-[var(--color-muted-foreground)]">
        <span className="font-medium uppercase">{message.kind}</span>
        {message.kind === 'response' && (
          <span className="flex items-center gap-2 tabular-nums">
            {message.modelName && <span>{message.modelName}</span>}
            <span>
              in {message.usage.inputTokens} ({message.usage.cacheReadTokens} cached) · out{' '}
              {message.usage.outputTokens}
            </span>
          </span>
        )}
      </div>
      <div className="space-y-3">
        {message.kind === 'request'
          ? message.parts.map((part) => <RequestPartView key={requestPartKey(part)} part={part} />)
          : message.parts.map((part) => (
              <ResponsePartView key={responsePartKey(part)} part={part} />
            ))}
      </div>
    </li>
  );
}

export function Transcript({ messages }: { messages: Message[] }) {
  if (!messages.length) {
    return <p className="text-sm text-[var(--color-muted-foreground)]">No messages yet.</p>;
  }
  return (
    <ul className="space-y-3">
      {messages.map((message) => (
        <MessageView key={message.id} message={message} />
      ))}
    </ul>
  );
}
